import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useLang } from "@/lib/i18n";

const ITEMS = [
  { path: "/", key: "home" },
  { path: "/qui-sommes-nous", key: "about" },
  { path: "/nos-activites", key: "activities" },
  { path: "/partenariats", key: "partnerships" },
  { path: "/contact", key: "contact" },
];

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const LangSwitch = ({ className = "" }) => (
    <div
      className={`flex items-stretch border border-line bg-white text-xs ${className}`}
    >
      {["fr", "en"].map((l, i) => (
        <button
          key={l}
          data-testid={`lang-switch-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-widest transition-colors duration-200 ${
            lang === l ? "bg-brand text-white" : "text-ink2 hover:text-ink"
          } ${i === 0 ? "border-r border-line" : ""}`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-line bg-white/92 backdrop-blur-md" data-testid="navbar">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <Logo className="h-9 w-auto" />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {ITEMS.map((it) => (
            <NavLink
              key={it.key}
              to={it.path}
              data-testid={`nav-link-${it.key}`}
              end={it.path === "/"}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-[0.16em] transition-colors duration-200 ${
                  isActive
                    ? "text-brand underline decoration-2 underline-offset-[6px]"
                    : "text-ink2 hover:text-ink"
                }`
              }
            >
              {t.nav[it.key]}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangSwitch className="hidden md:flex" />
          <Link
            to="/contact"
            data-testid="nav-cta"
            className="group hidden items-center gap-2 bg-brand px-5 py-2.5 font-display text-[12px] font-bold uppercase tracking-[0.1em] text-white transition-colors duration-200 hover:bg-branddark lg:inline-flex"
          >
            {t.nav.cta}
            <ArrowUpRight className="h-4 w-4" />
          </Link>
          <button
            data-testid="nav-burger"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            className="flex h-10 w-10 items-center justify-center border border-line bg-white text-ink lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 top-0 z-40 flex flex-col bg-white px-6 pb-10 pt-24"
          >
            <nav className="flex flex-col gap-1">
              {ITEMS.map((it, i) => (
                <motion.div
                  key={it.key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35 }}
                >
                  <NavLink
                    to={it.path}
                    data-testid={`drawer-link-${it.key}`}
                    end={it.path === "/"}
                    className={({ isActive }) =>
                      `flex items-center justify-between border-b border-line py-4 font-display text-2xl font-bold tracking-[-0.02em] ${
                        isActive ? "text-brand" : "text-ink"
                      }`
                    }
                  >
                    {t.nav[it.key]}
                    <span className="font-mono text-xs text-ink3">0{i + 1}</span>
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="mt-auto flex items-center justify-between pt-8">
              <LangSwitch className="flex" />
              <Link
                to="/contact"
                data-testid="drawer-cta"
                className="inline-flex items-center gap-2 bg-brand px-6 py-3 font-display text-[13px] font-bold uppercase tracking-[0.1em] text-white"
              >
                {t.nav.cta}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
